import { Component, OnDestroy, OnInit } from '@angular/core';
import { SessionService } from 'src/app/shared/services/session.service';
import { BookingService } from 'src/app/simulator-booking/services/booking.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseBookingModalComponent } from '../base-booking-modal.component';
import { FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Simulator } from 'src/app/simulator-booking/models/simulators';
import { AddonService } from 'src/app/simulator-booking/services/addon.service';
import { BookingAddon } from 'src/app/simulator-booking/models/booking-addon';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { BookingStatusEnum } from 'src/app/shared/models/booking-status.enum';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { OperatingHours } from 'src/app/simulator-booking/models/operating-hours';
import { BookingEventEmitters } from 'src/app/simulator-booking/booking-extensions/services/booking-event-emitters';
import { environment } from 'src/environments/environment';
import { BookingDurationService } from 'src/app/simulator-booking/services/booking-duration.service';
import { BookingDuration } from 'src/app/simulator-booking/models/booking-duration';

@Component({
    selector: 'booking-edit-modal',
    templateUrl: './booking-edit-modal.component.html',
    styleUrl: './booking-edit-modal.component.scss',
    providers: [DatePipe],
    standalone: false
})
export class BookingEditModalComponent extends BaseBookingModalComponent<BookingEditModalComponent> implements OnInit, OnDestroy {
  dateControl = new FormControl();
  timeSlot = new FormControl();
  availableDates: Date[] = [];
  availableTimeSlots: any[] = [];
  simulators: Simulator[] = [];
  bookingId: string;
  confirmationNumber: string;
  userAccount: any;
  paymentIntents: any;
  currentPrice: number;
  bookingPaymentIntent: any;
  availableBookingDurations: BookingDuration[] = [];

  constructor(protected sessionService: SessionService,
    protected bookingService: BookingService,
    protected addonService: AddonService,
    protected dialog: MatDialog,
    private bookingEventEmitters: BookingEventEmitters,
    private bookingDurationService: BookingDurationService,
    dialogRef: MatDialogRef<BookingEditModalComponent>
  ) {
    super(sessionService, bookingService, addonService, dialogRef, dialog);
  }

  ngOnInit(): void {
    this.getAddons();
    this.getCustomerPaymentMethod();
    this.getBookingDurations();
    this.priceOverrideSubscription = this.form.get('priceOverride').valueChanges.subscribe(value => {
      if (this.form.get('priceOverride').valid && value !== null) {
        this.form.get('totalPrice').setValue(value);
      } else {
        this.form.get('totalPrice').setValue(this.availableTimeSlots.length > 0 ? this.timeSlot.value.price : 0);
      }
    });

    if (this.form.get('duration').value !== null) {
      this.getTimeSlots();
    }
    this.currentPrice = this.form.get('totalPrice').value;
  }

  getBookingDurations() {
    this.bookingDurationService.getAllByOrganization({ organizationId: this.sessionService.getUser().organizationId }).subscribe({
      next: (result) => {
        this.availableBookingDurations = result;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${hours} hours ${remainingMinutes} minutes`;
  }

  ngOnDestroy() {
    if (this.priceOverrideSubscription) {
      this.priceOverrideSubscription.unsubscribe();
    }
  }

  getTimeSlots() {
    if (this.form.get('duration').value !== null) {
      this.isLoading = true;
      let data = {
        simulatorId: this.form.get('simulatorId').value,
        durationMinutes: this.form.get('duration').value,
        startDate: this.form.get('startTime').value,
        organizationId: this.sessionService.getUser().organizationId,
        bookingId: this.bookingId,
        operatingHours: this.sessionService.getUser().operatingHours ?
          JSON.parse(this.sessionService.getUser().operatingHours as unknown as string) as OperatingHours[] :
          [],
        timeZone: this.sessionService.getUser().timeZone
      };

      this.bookingService.getTimeSlots(data).subscribe({
        next: (result) => {
          this.availableTimeSlots = result;

          // Extract unique dates from available time slots
          this.availableDates = Array.from(
            new Set(
              this.availableTimeSlots.map(slot => {
                const d = new Date(slot.startTime);
                return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
              })
            )
          ).map(time => new Date(time));

          // Set dateControl to the matching date if possible
          const startTimeDate = new Date(this.form.get('startTime').value);
          const matchingDate = this.availableDates.find(date =>
            date.getFullYear() === startTimeDate.getFullYear() &&
            date.getMonth() === startTimeDate.getMonth() &&
            date.getDate() === startTimeDate.getDate()
          );
          this.dateControl.setValue(matchingDate || null);

          if (this.availableTimeSlots.length > 0) {
            const currentStartTime = new Date(this.form.get('startTime').value);
            const currentEndTime = new Date(this.form.get('endTime').value);

            const matchingSlot = this.availableTimeSlots.find(slot => {
              const slotStartTime = new Date(slot.startTime);
              const slotEndTime = new Date(slot.endTime);
              return slotStartTime.getTime() === currentStartTime.getTime() &&
                slotEndTime.getTime() === currentEndTime.getTime();
            });

            if (matchingSlot !== undefined) {
              this.timeSlot.setValue(matchingSlot);
              this.setBookingTimes(matchingSlot);
            } else {
              this.form.get('startTime').setValue(null);
              this.form.get('endTime').setValue(null);
            }
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
          this.setError(error, this.form);
        },
        complete: () => this.isLoading = false
      });
    }
  }

  setEndTimeAndTotalPrice(timeslot) {
    this.setBookingTimes(timeslot);
    this.calculateAndSetTotalPrice(timeslot);
  }

  private setBookingTimes(timeslot) {
    this.form.get('startTime').setValue(timeslot.startTime);
    this.form.get('endTime').setValue(timeslot.endTime);
  }

  private calculateAndSetTotalPrice(timeslot) {
    if(this.form.get('status').value === BookingStatusEnum.Scheduled) {
      return;
    }

    let totalPrice = this.userAccount.creditCardFeeType === CreditCardFeeType.Percentage ?
      timeslot.price * (1 + this.userAccount.creditCardFee / 100) :
      timeslot.price + this.userAccount.creditCardFee;

    if (this.selectedAddons?.length > 0) {
      totalPrice += this.selectedAddons.reduce((sum, addon) => sum + addon.price, 0);
    }

    this.form.get('totalPrice').setValue(totalPrice);
  }

  private async checkPriceDifference(): Promise<boolean> {
    if (this.form.get('hasPaid').value === true) {
      // Calculate new total with credit card fee
      const newPrice = this.form.get('totalPrice').value;
      let priceDifference = newPrice - this.currentPrice;

      if (priceDifference !== 0) {
        const dialogRef = this.dialog.open(AlertModalComponent);
        dialogRef.componentInstance.title = 'Price Change';
        if (priceDifference > 0) {
          dialogRef.componentInstance.body = `The customer will be charged an additional $${priceDifference.toFixed(2)}. Would you like to proceed?`;
        } else {
          dialogRef.componentInstance.body = `The customer will be refunded $${Math.abs(priceDifference).toFixed(2)}. Would you like to proceed?`;
        }
        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        return new Promise((resolve) => {
          dialogRef.afterClosed().subscribe(result => {
            resolve(result === 'Yes');
          });
        });
      }
    }
    return true;
  }

  async submit({ value, valid }) {
    if (valid) {
      if (value.status === BookingStatusEnum.Cancelled && value.hasPaid) {
        const dialogRef = this.dialog.open(AlertModalComponent);
        dialogRef.componentInstance.title = 'Process Refund';
        dialogRef.componentInstance.body = `By cancelling this booking, you will be refunding the full amount of $${value.totalPrice}.`;
        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;
        dialogRef.afterClosed().subscribe((response) => {
          if (response === 'Yes') {
            this.refund(value, false);
          }
        });
      } else {
        const shouldProceed = await this.checkPriceDifference();
        if (shouldProceed) {
          const priceDifference = this.form.get('totalPrice').value - this.currentPrice;

          if (priceDifference < 0 && this.paymentIntents.length > 0) {
            this.refund(value, true);
          } else if (priceDifference > 0 && this.form.get('hasPaid').value === true && this.paymentIntents.length > 0) {
            let data = {
              accountId: this.sessionService.getUser().stripeAccountId,
              options: {
                amount: priceDifference * 100,
                paymentMethod: this.paymentIntents.find(x => x.metadata.Parent === 'true').payment_method.id,
                customer: this.paymentIntents.find(x => x.metadata.Parent === 'true').customer,
                currency: 'usd',
                confirm: true,
                description: 'Additional payment for booking modification',
                receiptEmail: this.form.get('email').value,
                returnUrl: `${environment.dashboardLocation}/simulator-management/simulator-booking/bookings`,
                metadata: {
                  bookingId: this.bookingId,
                  additionalPaymentReason: 'Booking modification'
                }
              }
            };
            this.bookingService.processAdditionalPayment(data).subscribe({
              next: (response: any) => {
                this.updateBooking(value);
              },
              error: (error: any) => {
                console.error(error);
                this.isLoading = false;
              },
              complete: () => this.isLoading = false
            });
          } else {
            this.updateBooking(value);
          }
        }
      }
    }
  }

  private refund(value: any, isPartialRefund: boolean = false) {
    this.isLoading = true;
    let stripeChargeId;

    if (isPartialRefund) {
      value.refundAmount = Math.abs(this.form.get('totalPrice').value - this.currentPrice);
      value.refundReason = 'Booking modification';
      stripeChargeId = this.paymentIntents[this.paymentIntents.length - 1].latest_charge;
    } else {
      value.refundAmount = value.totalPrice;
      value.refundReason = 'Cancelled by Customer';
      value.totalPrice = 0;
      stripeChargeId = this.paymentIntents.find(x => x.metadata.Parent === 'true').latest_charge;
    }

    value.lastEditedBy = this.sessionService.getUser().id;
    value.organizationId = this.sessionService.getUser().organizationId;
    value.addons = value.addons;
    value.id = this.bookingId;
    value.timeZone = this.sessionService.getUser().timeZone;
    value.stripePaymentIntentId = this.bookingPaymentIntent;

    let data = {
      stripeAccountId: this.sessionService.getUser().stripeAccountId,
      stripeChargeId: stripeChargeId,
      booking: value,
      isPartialRefund
    };

    this.bookingService.refund(data).subscribe({
      next: (response: any) => {
        if (response.status === 200) {
          if (isPartialRefund) {
            this.updateBooking(value);
          } else {
            this.dialogRef.close('refund');
          }
        } else {
          this.dialogRef.close('error');
        }
      },
      error: (error: any) => {
        console.error(error);
        this.isLoading = false;
      },
      complete: () => {
        this.bookingEventEmitters.updateBookingEvent.emit();
        this.isLoading = false;
      }
    });
  }

  updateBooking(value) {
    this.isLoading = true;

    const bookingAddons: BookingAddon[] = [];
    if (this.selectedAddons?.length > 0) {
      this.selectedAddons.forEach(addon => {
        const bookingAddon = new BookingAddon();
        bookingAddon.addonId = addon.id;
        bookingAddon.organizationId = this.sessionService.getUser().organizationId;
        bookingAddon.lastEditedBy = this.sessionService.getUserId();
        bookingAddon.bookingId = this.bookingId;
        bookingAddons.push(bookingAddon);
      });
    }

    value.addons = bookingAddons;
    value.startTime = this.stringHelpers.normalizeDateTime(value.startTime);
    value.endTime = this.stringHelpers.normalizeDateTime(value.endTime);
    value.id = this.bookingId;
    value.lastEditedBy = this.sessionService.getUserId();
    value.timeZone = this.sessionService.getUser().timeZone;
    value.stripePaymentIntentId = this.bookingPaymentIntent;

    this.bookingService.update(value.id, value).subscribe({
      next: (result) => {
        if (result !== null || result !== undefined) {
          this.dialogRef.close("save");
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error(error);
        this.setError(error, this.form);
      },
      complete: () => this.isLoading = false
    });
  }


  onAddonSelectionChange(event: any) {
    const previousAddons = this.selectedAddons || [];
    this.selectedAddons = event.value;

    // Always set the addons value, even if empty array
    this.form.get('addons').setValue(this.selectedAddons);

    // Calculate price difference from addon changes
    const removedAddons = previousAddons.filter(prev =>
      !this.selectedAddons.some(curr => curr.id === prev.id)
    );
    const addedAddons = this.selectedAddons.filter(curr =>
      !previousAddons.some(prev => prev.id === curr.id)
    );

    let bookingStatus = this.form.get('status').value;
    if(bookingStatus === BookingStatusEnum.Scheduled) {
      return;
    }

    let currentTotal = this.form.get('totalPrice').value;
    removedAddons.forEach(addon => {
      currentTotal -= addon.price;
    });
    addedAddons.forEach(addon => {
      currentTotal += addon.price;
    });

    this.form.get('totalPrice').setValue(currentTotal);
  }

  getAddons() {
    this.addonService.getAllByOrganization({ organizationId: this.sessionService.getUser().organizationId }).subscribe({
      next: (result) => {
        this.availableAddons = result.results;
        if (this.form.get('addons').value?.length > 0) {
          this.selectedAddons = this.availableAddons.filter(addon =>
            this.form.get('addons').value.some(selected => selected.addonId === addon.id)
          );
          this.form.get('addons').setValue(this.selectedAddons);
        }
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  getCustomerPaymentMethod() {
    if (this.form.get('hasPaid').value) {
      this.isLoading = true;
      let data = {
        accountId: this.sessionService.getUser().stripeAccountId,
        options: {
          query: `metadata['BookingId']:'${this.bookingId}'`
        }
      };
      this.bookingService.getPaymentMethodForRefundOrAdditionalPayment(data).subscribe({
        next: (paymentIntent: any) => {
          this.paymentIntents = paymentIntent;
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error(error);
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    }else{
      this.paymentIntents = [];
    }
  }

}
